import { Controller } from "@hotwired/stimulus"

// Client-side browse for the tags index: local search, per-letter sort,
// smooth scroll-to-letter, and deselect-on-scroll. The full tag list is
// server-rendered (no pagination), so nothing here hits the server — the
// controls only filter/reorder existing nodes and degrade to no-ops without JS.
export default class extends Controller<HTMLElement> {
  static targets = [
    "input",
    "clear",
    "section",
    "pill",
    "empty",
    "resultsInfo",
    "resultsCount",
    "resultsLabel",
    "sortButton",
    "railLink",
  ]

  declare readonly inputTarget: HTMLInputElement
  declare readonly hasClearTarget: boolean
  declare readonly clearTarget: HTMLElement
  declare readonly sectionTargets: HTMLElement[]
  declare readonly pillTargets: HTMLElement[]
  declare readonly emptyTarget: HTMLElement
  declare readonly resultsInfoTarget: HTMLElement
  declare readonly resultsCountTarget: HTMLElement
  declare readonly resultsLabelTarget: HTMLElement
  declare readonly sortButtonTargets: HTMLElement[]
  declare readonly railLinkTargets: HTMLElement[]

  // True only while a rail-initiated smooth scroll is in flight, so the scroll
  // listener can tell programmatic scrolls from the user's own scrolling.
  autoScrolling = false
  scrollEndTimer?: number
  boundScroll = this.handleScroll.bind(this)

  // The server renders each section in Popular order (the canonical rule); this
  // snapshots it so the "Popular" button restores it instead of re-deriving it.
  originalOrder = new Map<HTMLElement, HTMLElement[]>()

  connect() {
    this.originalOrder = new Map(
      this.sectionTargets.map((section) => [
        section,
        [...section.querySelectorAll<HTMLElement>('[data-tags-browse-target="pill"]')],
      ]),
    )
    window.addEventListener("scroll", this.boundScroll, { passive: true })
  }

  disconnect() {
    window.removeEventListener("scroll", this.boundScroll)
  }

  filter() {
    const query = this.inputTarget.value.trim().toLowerCase()
    let visible = 0

    for (const pill of this.pillTargets) {
      const matches = (pill.dataset.searchText ?? "").includes(query)
      pill.classList.toggle("hidden", !matches)
      if (matches) visible += 1
    }

    // A letter section with no visible pill hides; its head count tracks the
    // visible matches, and its rail link goes dim and unclickable so navigation
    // stays in sync with what's on screen.
    for (const section of this.sectionTargets) {
      const shown = section.querySelectorAll('[data-tags-browse-target="pill"]:not(.hidden)').length
      section.classList.toggle("hidden", shown === 0)

      const count = section.querySelector<HTMLElement>('[data-tags-browse-target="sectionCount"]')
      if (count) {
        const word = shown === 1 ? (count.dataset.one ?? "") : (count.dataset.other ?? "")
        count.textContent = `${shown} ${word}`
      }

      const link = this.railLinkTargets.find((l) => l.dataset.letter === section.dataset.letter)
      link?.classList.toggle("opacity-30", shown === 0)
      link?.classList.toggle("pointer-events-none", shown === 0)
    }

    this.emptyTarget.classList.toggle("hidden", visible > 0)
    this.emptyTarget.classList.toggle("flex", visible === 0)
    this.resultsInfoTarget.classList.toggle("hidden", query === "")
    this.resultsCountTarget.textContent = String(visible)
    this.resultsLabelTarget.textContent =
      visible === 1
        ? (this.resultsLabelTarget.dataset.one ?? "")
        : (this.resultsLabelTarget.dataset.other ?? "")
    if (this.hasClearTarget) this.clearTarget.classList.toggle("hidden", query === "")
  }

  clearSearch() {
    this.inputTarget.value = ""
    this.filter()
    this.inputTarget.focus()
  }

  sort(event: Event) {
    const button = event.currentTarget as HTMLElement
    const key = button.dataset.sort ?? "popular"

    // Reorder within each letter section independently — grouping never moves.
    // "Popular" restores the server's order; the rest re-sort the pills.
    for (const section of this.sectionTargets) {
      const list = section.querySelector('[data-tags-browse-target="list"]')
      if (!list) continue
      const pills =
        key === "popular"
          ? (this.originalOrder.get(section) ?? [])
          : [...list.querySelectorAll<HTMLElement>('[data-tags-browse-target="pill"]')].sort(
              (a, b) => this.compare(a, b, key),
            )
      pills.forEach((pill) => list.appendChild(pill))
    }

    for (const candidate of this.sortButtonTargets) {
      const active = candidate === button
      candidate.classList.toggle("btn-primary", active)
      candidate.classList.toggle("btn-ghost", !active)
      candidate.classList.toggle("text-muted-content", !active)
      candidate.setAttribute("aria-pressed", String(active))
    }
  }

  // Only the client-derivable sorts live here; "Popular" is the server's order
  // (restored from the snapshot), so it has no rule to mirror.
  compare(a: HTMLElement, b: HTMLElement, key: string) {
    if (key === "newest") {
      return Number(b.dataset.published ?? 0) - Number(a.dataset.published ?? 0)
    }
    return (a.dataset.term ?? "").localeCompare(b.dataset.term ?? "")
  }

  jump(event: Event) {
    event.preventDefault()
    const link = event.currentTarget as HTMLElement
    const section = this.sectionTargets.find((s) => s.dataset.letter === link.dataset.letter)
    if (!section) return

    this.setActive(link)
    // Reflect the jump in the URL so the section is deep-linkable and shareable,
    // without the abrupt jump a bare href click would cause.
    const href = link.getAttribute("href")
    if (href) history.replaceState(null, "", href)
    this.autoScrolling = true
    section.scrollIntoView({ behavior: "smooth", block: "start" })

    // Clear the programmatic flag when the smooth scroll settles. scrollend is
    // the precise signal; the timeout is a safety net for when it never fires
    // (target already in view, or smooth scrolling suppressed) so a later user
    // scroll can still deselect.
    const done = () => {
      window.clearTimeout(this.scrollEndTimer)
      this.autoScrolling = false
    }
    window.clearTimeout(this.scrollEndTimer)
    this.scrollEndTimer = window.setTimeout(done, 1000)
    if ("onscrollend" in window) window.addEventListener("scrollend", done, { once: true })
  }

  handleScroll() {
    // Ignore the rail's own smooth scroll; a real user scroll clears the
    // selected letter (the prototype leaves it stuck — this fixes that).
    if (this.autoScrolling) return
    this.clearActive()
  }

  setActive(link: HTMLElement) {
    for (const candidate of this.railLinkTargets) {
      if (candidate === link) {
        candidate.setAttribute("aria-current", "true")
      } else {
        candidate.removeAttribute("aria-current")
      }
    }
  }

  clearActive() {
    for (const candidate of this.railLinkTargets) candidate.removeAttribute("aria-current")
  }
}
